import React, { useMemo } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import type { GlobalSideNavigationDataProviderProps } from '../common/types';

import { type GlobalSideNavigationDataProviderQuery } from './__generated__/GlobalSideNavigationDataProviderQuery.graphql';

export function GlobalSideNavigationDataProvider(props: GlobalSideNavigationDataProviderProps) {
	// We need to use RelayEnvironmentProvider because the platform-version points to AGG, which is where the data can be accessed from.
	return (
		<RelayEnvironmentProvider>
			<RawGlobalSideNavigationDataProvider {...props} />
		</RelayEnvironmentProvider>
	);
}

/**
 * This component is exposed for testing purposes, but is wrapped by GlobalSideNavigationDataProvider
 * @example ```
 * <RelayEnvironmentProvider environment={mockEnv}>
 *   <RawGlobalSideNavigationDataProvider entityAri={} ownerAri={}>
 *     {({queryRef}) => <GlobalSideNavigation queryRef={queryRef} ... />}
 *   </RawGlobalSideNavigationDataProvider>
 * </RelayEnvironmentProvider>
 * ```
 */
export function RawGlobalSideNavigationDataProvider(props: GlobalSideNavigationDataProviderProps) {
	const { children, entityAri, ownerAri } = props;
	const queryVariables = useMemo(
		() => ({ entityAriUserSideNavSettings: entityAri, ownerAri }),
		[entityAri, ownerAri],
	);

	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	const gsnData = useLazyLoadQuery<GlobalSideNavigationDataProviderQuery>(
		graphql`
			query GlobalSideNavigationDataProviderQuery(
				$entityAriUserSideNavSettings: ID!
				$ownerAri: ID!
			) {
				...GlobalSideNavigation
			}
		`,
		queryVariables,
	);
	/* eslint-enable @atlassian/relay/must-colocate-fragment-spreads */

	return <>{children({ queryRef: gsnData })}</>;
}
